import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/printful/products/${id}`);
        if (cancelled) return;
        const p = res.data.product;
        setProduct(p);
        setVariant(p.variants?.[0] || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onAddToCart = () => {
    if (!variant) return;
    toast.success(`Added to cart: ${product.name} (${variant.name}) ×${qty}`, {
      description: "Checkout coming soon — we'll be in touch.",
    });
  };

  if (loading) {
    return (
      <div className="container-edge py-32" data-testid="product-loading">
        <div className="flex items-center gap-3 text-soft">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container-edge py-32" data-testid="product-error">
        <Link to="/store" className="inline-flex items-center text-sm text-soft hover:text-ocean">
          <ArrowLeft size={14} className="mr-2" /> Back to shop
        </Link>
        <h2 className="mt-6 text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>Product not found</h2>
        <p className="mt-3 text-soft">{error || "We couldn't load this product."}</p>
      </div>
    );
  }

  const displayImg = variant?.image || product.thumbnail_url;
  const price = variant ? `$${variant.retail_price} ${variant.currency}` : "";

  return (
    <div data-testid="product-detail-page">
      <section className="container-edge pt-12 pb-6">
        <Link to="/store" className="inline-flex items-center text-sm text-soft hover:text-ocean transition-colors" data-testid="back-to-shop">
          <ArrowLeft size={14} className="mr-2" /> Back to shop
        </Link>
      </section>

      <section className="container-edge py-12" data-testid="product-detail">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 zoom-img bg-linen aspect-square overflow-hidden">
            {displayImg ? (
              <img src={displayImg} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-soft">No image</div>
            )}
          </div>
          <div className="lg:col-span-5">
            <p className="overline">Sonia Rolland Merch</p>
            <h1 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              {product.name}
            </h1>
            <p className="mt-6 text-3xl text-ocean" data-testid="product-price">{price}</p>

            {product.variants && product.variants.length > 1 && (
              <div className="mt-10">
                <label className="overline">Variant</label>
                <select
                  className="mt-3 w-full border border-soft bg-paper py-3 px-4 text-sm focus:outline-none focus:border-sand"
                  value={variant?.id || ""}
                  onChange={(e) => {
                    const v = product.variants.find((x) => String(x.id) === e.target.value);
                    setVariant(v || null);
                  }}
                  data-testid="product-variant-select"
                >
                  {product.variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — ${v.retail_price} {v.currency}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-8">
              <label className="overline">Quantity</label>
              <div className="mt-3 flex items-center gap-3">
                <button
                  className="w-10 h-10 border border-soft hover:border-ocean transition-colors"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  data-testid="qty-decrement"
                  type="button"
                >
                  –
                </button>
                <span className="w-12 text-center" data-testid="product-qty">{qty}</span>
                <button
                  className="w-10 h-10 border border-soft hover:border-ocean transition-colors"
                  onClick={() => setQty((q) => q + 1)}
                  data-testid="qty-increment"
                  type="button"
                >
                  +
                </button>
              </div>
            </div>

            <button
              onClick={onAddToCart}
              className="btn-primary mt-10 w-full"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={16} className="mr-2" /> Add to cart
            </button>

            <p className="mt-6 text-xs text-soft">
              Printed on demand and shipped to you. Each piece is made when you order, so allow
              a few extra days for production. ✦ California-inspired, globally shipped.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
